import type { Id } from '@contracts/common';

/**
 * "Show me this card" from outside the Workplace (T2, 2026-09-30): a task, note or work chip in Hana's answer. The
 * top-level Workplace registers a handler while it is mounted, so a chip on it focuses the card in place — selected and
 * brought into view, no modal, no navigation. Anywhere else the chip goes to `/workplace?focus=<id>`, which the page
 * reads on load, focuses, and takes out of the URL. A project's embedded board registers nothing.
 */

export type FocusOptions = {
  /** Move DOM focus to the card: the sheet that held the chip has closed (a narrow view). */
  moveFocus: boolean;
};
export type FocusHandler = (id: Id, options: FocusOptions) => void;

export const FOCUS_PARAM = 'focus';
export const focusHref = (id: Id): string => `/workplace?${FOCUS_PARAM}=${encodeURIComponent(id)}`;

let handler: FocusHandler | null = null;

/** Registers the page's handler; the returned function lets go of it (only if it is still the one registered). */
export function registerWorkplaceFocus(next: FocusHandler): () => void {
  handler = next;
  return () => { if (handler === next) handler = null; };
}

/** Focuses the card on the Workplace on screen, or goes there with `?focus=`. */
export function focusWorkplaceItem(
  id: Id, options: FocusOptions,
  navigate: (to: string, opts: { state: { focusMove: boolean } }) => void,
): void {
  if (handler !== null) { handler(id, options); return; }
  navigate(focusHref(id), { state: { focusMove: options.moveFocus } });
}
